import { extractErrorMessage } from "@/lib/error"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"

import { organizationService } from "../services/organization-service"
import { Organization } from "../types"

export const ORGANIZATION_QUERY_KEY_PREFIX = "organization"

export const useOrganization = (organizationId: string | undefined) => {
  return useQuery<Organization, Error, Organization, [string, string | undefined]>({
    queryKey: [ORGANIZATION_QUERY_KEY_PREFIX, organizationId],
    queryFn: async () => {
      if (!organizationId) {
        throw new Error("Attempted to fetch organization without an ID.")
      }
      return organizationService.getOrganizationById(organizationId)
    },
    enabled: !!organizationId,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  })
}

interface UpdateOrganizationVariables {
  organizationId: string
  payload: Partial<Pick<Organization, "name" | "whatsapp" | "timezone">>
}

export const useUpdateOrganizationDetails = () => {
  const queryClient = useQueryClient()

  return useMutation<
    Organization, // Type of data returned by the mutationFn
    Error, // Type of error
    UpdateOrganizationVariables // Type of variables passed to mutate
  >({
    mutationFn: ({ organizationId, payload }) =>
      organizationService.updateOrganizationDetails(organizationId, payload),
    onSuccess: (data, variables) => {
      queryClient.setQueryData([ORGANIZATION_QUERY_KEY_PREFIX, variables.organizationId], data)
    },
    onError: error => {
      const errorMessage = extractErrorMessage(
        error,
        "Erro ao atualizar a organização. Tente novamente."
      )
      console.error("Error updating organization details:", error)
      toast.error(errorMessage)
    },
  })
}
